import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wordmark } from "@/components/Navbar";
import { API, CONTACT, EVENT_TYPES, TRAILERS, formatApiError } from "@/lib/site";

const STEPS = ["Trailer", "Event Details", "Dates", "Confirm"];

const inputCls =
  "rounded-none border-stone-300 bg-white py-6 text-stone-900 placeholder:text-stone-400 focus-visible:ring-brand-bronze";

const empty = {
  trailer: "",
  event_type: "",
  venue_address: "",
  city: "",
  guests: "",
  start_date: "",
  end_date: "",
  name: "",
  email: "",
  phone: "",
  notes: "",
};

export default function BookingPage() {
  const [params] = useSearchParams();
  const initialTrailer = params.get("trailer") || "";
  const [step, setStep] = useState(initialTrailer ? 1 : 0);
  const [form, setForm] = useState({ ...empty, trailer: initialTrailer });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/availability`).then((r) => setBlocks(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });
  const selectedTrailer = useMemo(() => TRAILERS.find((t) => t.id === form.trailer), [form.trailer]);
  const relevantBlocks = useMemo(
    () => blocks.filter((b) => b.trailer === "all" || b.trailer === form.trailer),
    [blocks, form.trailer]
  );
  const datesOverlap = (s, e) => relevantBlocks.some((b) => s <= b.end_date && e >= b.start_date);
  const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

  const validateStep = () => {
    if (step === 0 && !form.trailer) return "Please choose a trailer to continue.";
    if (step === 1 && (!form.event_type || !form.venue_address || !form.city))
      return "Please add your event type, venue address, and city.";
    if (step === 2) {
      if (!form.start_date || !form.end_date) return "Please choose both a start and end date.";
      if (form.end_date < form.start_date) return "Your end date can't be before the start date.";
      if (datesOverlap(form.start_date, form.end_date))
        return "Those dates overlap an existing booking for this trailer — please pick different dates.";
    }
    if (step === 3 && (!form.name || !form.email || !form.phone))
      return "Please add your name, email, and phone number.";
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) return toast.error(err);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    const err = validateStep();
    if (err) return toast.error(err);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, form);
      setDone(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="flex min-h-screen flex-col bg-stone-50">
        <BookingHeader />
        <main className="flex flex-1 items-center justify-center px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-xl border border-brand-bronze/30 bg-white p-10 text-center"
            data-testid="booking-success"
          >
            <CheckCircle2 className="mx-auto h-14 w-14 text-brand-bronze-dark" aria-hidden="true" />
            <h1 className="mt-6 font-serif text-3xl font-light text-stone-900 sm:text-4xl">Request received</h1>
            <p className="mt-4 text-base font-light leading-relaxed text-stone-600">
              Thank you, {form.name.split(" ")[0]}. Your booking request for{" "}
              <span className="font-normal text-stone-900">{selectedTrailer?.name}</span> is with our team — we'll
              confirm availability and delivery details for {form.city} shortly.
            </p>
            <p className="mt-6 font-mono text-xs tracking-widest text-stone-400" data-testid="booking-reference">
              REF {done.slice(0, 8).toUpperCase()}
            </p>
            <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
              <Link to="/" data-testid="booking-success-home-link" className="bg-stone-900 px-6 py-3 text-sm tracking-wide text-stone-50 transition-colors duration-300 hover:bg-brand-bronze hover:text-stone-900">
                Back to Home
              </Link>
              <a href={CONTACT.phoneHref} data-testid="booking-success-call-link" className="border border-stone-300 px-6 py-3 text-sm tracking-wide text-stone-900 transition-colors duration-300 hover:border-brand-bronze">
                Call {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-stone-50" data-testid="booking-page">
      <BookingHeader />
      <main className="mx-auto w-full max-w-2xl flex-1 px-6 pb-24 pt-32">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-brand-bronze-dark">Online Booking</p>
        <h1 className="mt-3 font-serif text-3xl font-light text-stone-900 sm:text-4xl">Reserve your trailer</h1>

        <ol className="mt-10 flex items-center" data-testid="booking-stepper">
          {STEPS.map((label, i) => (
            <li key={label} className={`flex items-center ${i < STEPS.length - 1 ? "flex-1" : ""}`}>
              <span className="flex items-center gap-3">
                <span
                  className={`flex h-9 w-9 items-center justify-center border text-sm transition-colors duration-300 ${
                    i < step
                      ? "border-brand-bronze bg-brand-bronze text-stone-900"
                      : i === step
                        ? "border-stone-900 bg-stone-900 text-stone-50"
                        : "border-stone-300 text-stone-400"
                  }`}
                  data-testid={`step-indicator-${i}`}
                >
                  {i < step ? <Check className="h-4 w-4" aria-hidden="true" /> : i + 1}
                </span>
                <span className={`hidden text-xs font-semibold tracking-[0.15em] uppercase sm:block ${i === step ? "text-stone-900" : "text-stone-400"}`}>
                  {label}
                </span>
              </span>
              {i < STEPS.length - 1 && <span className={`mx-4 h-px flex-1 ${i < step ? "bg-brand-bronze" : "bg-stone-300"}`} />}
            </li>
          ))}
        </ol>

        <div className="mt-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 0 && (
                <div className="space-y-5" data-testid="booking-step-trailer">
                  {TRAILERS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setForm({ ...form, trailer: t.id })}
                      data-testid={`booking-trailer-option-${t.id}`}
                      aria-pressed={form.trailer === t.id}
                      className={`flex w-full items-center gap-6 border bg-white p-4 text-left transition-colors duration-300 ${
                        form.trailer === t.id ? "border-brand-bronze" : "border-stone-200 hover:border-stone-400"
                      }`}
                    >
                      <img src={t.exterior} alt={t.name} className="h-20 w-28 shrink-0 object-cover" />
                      <span className="flex-1">
                        <span className="block font-serif text-xl font-light text-stone-900">{t.name}</span>
                        <span className="mt-1 block text-xs font-semibold tracking-[0.2em] uppercase text-brand-bronze-dark">{t.tag}</span>
                      </span>
                      <span className={`flex h-6 w-6 items-center justify-center border ${form.trailer === t.id ? "border-brand-bronze bg-brand-bronze" : "border-stone-300"}`}>
                        {form.trailer === t.id && <Check className="h-4 w-4 text-stone-900" aria-hidden="true" />}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5" data-testid="booking-step-event">
                  <Select value={form.event_type} onValueChange={set("event_type")}>
                    <SelectTrigger className={inputCls} data-testid="booking-event-type-select" aria-label="Event type">
                      <SelectValue placeholder="Event type *" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`booking-event-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Venue address *" value={form.venue_address} onChange={set("venue_address")} className={inputCls} data-testid="booking-venue-input" aria-label="Venue address" />
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Input placeholder="City / town *" value={form.city} onChange={set("city")} className={inputCls} data-testid="booking-city-input" aria-label="City or town" />
                    <Input placeholder="Estimated guests" value={form.guests} onChange={set("guests")} className={inputCls} data-testid="booking-guests-input" aria-label="Estimated guests" />
                  </div>
                  <Textarea
                    placeholder="Delivery notes — access, surface, timing, anything we should know."
                    value={form.notes}
                    onChange={set("notes")}
                    className={`${inputCls} min-h-24`}
                    data-testid="booking-notes-input"
                    aria-label="Delivery notes"
                  />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5" data-testid="booking-step-dates">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block text-xs font-semibold tracking-[0.2em] uppercase text-stone-500">Delivery / start date *</span>
                      <Input type="date" required value={form.start_date} onChange={set("start_date")} className={inputCls} data-testid="booking-start-date-input" />
                    </label>
                    <label className="block">
                      <span className="mb-2 block text-xs font-semibold tracking-[0.2em] uppercase text-stone-500">Pickup / end date *</span>
                      <Input type="date" required value={form.end_date} onChange={set("end_date")} className={inputCls} data-testid="booking-end-date-input" />
                    </label>
                  </div>
                  {relevantBlocks.length > 0 && (
                    <div className="border border-stone-200 bg-white p-5" data-testid="booking-blocked-dates">
                      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-stone-500">
                        Already booked{selectedTrailer ? ` for ${selectedTrailer.name}` : ""}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {relevantBlocks.map((b) => (
                          <span
                            key={b.id}
                            className="border border-brand-bronze/40 bg-brand-bronze-light/30 px-3 py-1.5 text-xs tracking-wide text-stone-700"
                            data-testid={`blocked-range-${b.id.slice(0, 8)}`}
                          >
                            {fmtDate(b.start_date)} – {fmtDate(b.end_date)}
                            {b.note ? ` · ${b.note}` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="border border-brand-bronze/30 bg-brand-bronze-light/30 p-5 text-sm font-light leading-relaxed text-stone-600" data-testid="booking-date-note">
                    Multi-day rentals are welcome — just select your full range. We'll confirm exact delivery and
                    pickup windows, plus any delivery fee for your location, before anything is finalized.
                  </p>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5" data-testid="booking-step-confirm">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="booking-name-input" aria-label="Full name" />
                    <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="booking-email-input" aria-label="Email" />
                  </div>
                  <Input required type="tel" placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="booking-phone-input" aria-label="Phone" />
                  <dl className="border border-stone-200 bg-white p-6 text-sm" data-testid="booking-review">
                    {[
                      ["Trailer", selectedTrailer?.name],
                      ["Event", form.event_type],
                      ["Venue", `${form.venue_address}, ${form.city}`],
                      ["Dates", `${form.start_date} → ${form.end_date}`],
                      ["Guests", form.guests || "—"],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-6 border-b border-stone-100 py-3 last:border-0">
                        <dt className="text-xs font-semibold tracking-[0.2em] uppercase text-stone-400">{k}</dt>
                        <dd className="text-right font-light text-stone-800">{v || "—"}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="text-xs font-light leading-relaxed text-stone-400">
                    Submitting sends a booking request — not a payment. We'll confirm availability, final pricing, and
                    any delivery fees before your booking is secured.
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-12 flex items-center justify-between">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            data-testid="booking-back-button"
            className="inline-flex items-center gap-2 px-2 py-3 text-sm tracking-wide text-stone-600 transition-colors duration-300 hover:text-stone-900 disabled:invisible"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              data-testid="booking-next-button"
              className="group inline-flex items-center gap-3 bg-stone-900 px-8 py-4 text-sm tracking-wide text-stone-50 transition-colors duration-300 hover:bg-brand-bronze hover:text-stone-900"
            >
              Continue <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={sending}
              data-testid="booking-submit-button"
              className="inline-flex items-center gap-3 bg-stone-900 px-8 py-4 text-sm tracking-wide text-stone-50 transition-colors duration-300 hover:bg-brand-bronze hover:text-stone-900 disabled:opacity-60"
            >
              {sending ? "Submitting…" : "Submit Booking Request"}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

function BookingHeader() {
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-stone-200/60 bg-stone-50/85 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" data-testid="booking-logo-link" aria-label="Back to LooXury Rentals home">
          <Wordmark />
        </Link>
        <a href={CONTACT.phoneHref} data-testid="booking-phone-link" className="text-sm tracking-wide text-stone-700 transition-colors duration-300 hover:text-brand-bronze-dark">
          Questions? {CONTACT.phoneDisplay}
        </a>
      </div>
    </header>
  );
}
